/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useRouter } from "next/router";
import _range from "lodash/range";
import { colors } from "@mytutor/shared-react-web-components/lib/theme";
import urlParameterController from "../../util/urlParameterController";

const paginatorNumberStyle = (active) => css`
  display: inline-block;
  width: 24px;
  text-align: center;
  cursor: pointer;
  color: ${active ? colors.neutral1 : colors.primary5};
  background-color: ${active ? colors.primary5 : colors.neutral1};
  border-radius: 6px;
  &:hover {
    color: ${colors.primary6};
    background-color: ${colors.neutral2};
  }
`;

const paginatorArrowStyle = css`
  background-color: ${colors.neutral1};
  color: ${colors.primary5};
  border-radius: 6px;
  font-size: 1.5rem;
  font-weight: 200;
  padding: 6px 12px;
  cursor: pointer;
  &:hover {
    color: ${colors.primary6};
    background-color: ${colors.neutral2};
  }
`;

const paginatorStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  a {
    margin: 0 4px;
  }
`;

const Pagination = ({ paginationData }) => {
  const activePage = paginationData.pageNumber;
  const totalPages = paginationData.numberOfPages;
  const pageNeighbours = 2;
  const LEFT_PAGE = "LEFT";
  const RIGHT_PAGE = "RIGHT";

  const router = useRouter();
  /**
   * Let's say we have 10 pages and we set pageNeighbours to 2
   * Given that the current page is 6
   * The pagination control will look like the following:
   *
   * (1) < {4 5} [6] {7 8} > (10)
   *
   * (x) => terminal pages: first and last page(always visible)
   * [x] => represents current page
   * {...x} => represents page neighbours
   */
  const fetchPageNumbers = () => {
    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
     */
    const totalNumbers = pageNeighbours * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(1, activePage - pageNeighbours);
      const endPage = Math.min(totalPages, activePage + 1 + pageNeighbours);

      let pages = _range(startPage, endPage);

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 1;
      const hasRightSpill = totalPages - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      let extraPages;
      switch (true) {
        // handle: < {5 6} [7] {8 9}
        case hasLeftSpill && !hasRightSpill: {
          extraPages = _range(startPage - spillOffset, startPage - 1);
          pages = [LEFT_PAGE, ...extraPages, ...pages];
          break;
        }

        // handle: {2 3} [4] {5 6} >
        case !hasLeftSpill && hasRightSpill: {
          extraPages = _range(endPage, endPage + spillOffset - 1);
          pages = [...pages, ...extraPages, RIGHT_PAGE];

          break;
        }

        // handle: < {4 5} [6] {7 8} >
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
          break;
        }
      }

      return pages;
    }

    return _range(1, totalPages +1);
  };

  const pages = fetchPageNumbers();

  return (
    <div className="pagination" css={paginatorStyle} data-test="pagination">
      {pages.map((page, index) => {
        if (page === LEFT_PAGE)
          return (
            <a
              key={index + "left-chevron"}
              onClick={() => {
                // goes to previous page
                const newPage = activePage + 1 - pageNeighbours;
                const path = router.asPath;
                router.push(
                  urlParameterController("PAGINATION", newPage, path)
                );
              }}
              aria-label="Previous"
            >
              <p css={paginatorArrowStyle}>&#8592;</p>
            </a>
          );
        if (page === RIGHT_PAGE)
          return (
            <a
              key={index + "left-chevron"}
              onClick={() => {
                // goes to next page
                const newPage = activePage - 1 + pageNeighbours;
                const path = router.asPath;
                router.push(
                  urlParameterController("PAGINATION", newPage, path)
                );
              }}
              aria-label="Next"
            >
              <p css={paginatorArrowStyle}>&#8594;</p>
            </a>
          );
        return (
          <a
            key={index + page}
            onClick={() => {
              const path = router.asPath;
              router.push(urlParameterController("PAGINATION", page, path));
            }}
          >
            <p css={paginatorNumberStyle(activePage === page)}>{`${page}`}</p>
          </a>
        );
      })}
    </div>
  );
};

export default Pagination;
